import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useAppDispatch } from "src/common/state/hooks";
import AgentConfigFields, { AgentFormData } from "src/features/rag/components/AgentConfigFields";
import { fetchAgent } from "src/features/rag/api/ragApi";
import {
  createAgentThunk,
  updateAgentThunk,
  deleteAgentThunk,
} from "src/features/rag/state/ragSlice";
import { setAlert } from "src/features/alert/state/alertsSlice";
import ModalConfirmation from "src/common/components/Modal/ModalConfirmation";
import { openConfirmationModal } from "src/common/state/slice/modal/modalSlice";
import Spinner from "src/common/components/Loader/Spinner";

const DELETE_AGENT_MODAL_ID = "delete-rag-agent-confirmation";

const AgentForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const isEdit = !!id;
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<AgentFormData>({
    defaultValues: {
      name: "",
      description: "",
      system_prompt: "",
      llm_model: "mistral-large-latest",
      temperature: 0.3,
      icon: "default",
    },
  });

  useEffect(() => {
    if (isEdit && id) {
      const load = async () => {
        try {
          const agent = await fetchAgent(Number(id));
          reset({
            name: agent.name,
            description: agent.description || "",
            system_prompt: agent.system_prompt || "",
            llm_model: agent.llm_model,
            temperature: agent.temperature,
            icon: agent.icon,
          });
        } catch (err) {
          console.error("Failed to load agent:", err);
          navigate("/agents");
        } finally {
          setLoading(false);
        }
      };
      load();
    }
  }, [id, isEdit, navigate, reset]);

  const onSubmit = async (data: AgentFormData) => {
    setSubmitting(true);
    try {
      if (isEdit && id) {
        await dispatch(updateAgentThunk({ id: Number(id), data })).unwrap();
        dispatch(setAlert({ type: "success", msg: t("pages.agents.agentSaved") }));
        navigate(`/agents/${id}`);
      } else {
        const agent = await dispatch(createAgentThunk(data)).unwrap();
        dispatch(setAlert({ type: "success", msg: t("pages.agents.agentCreated") }));
        navigate(`/agents/${agent.id}`);
      }
    } catch (err: any) {
      // rejectWithValue rethrows the backend message as a plain string via unwrap()
      const msg = typeof err === "string" ? err : err?.response?.data?.error?.message || "";
      if (msg.includes("Agent limit")) {
        dispatch(setAlert({ type: "error", msg: t("pages.agents.quotaAgentsExceeded") }));
      } else {
        console.error("Failed to save agent:", err);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      await dispatch(deleteAgentThunk(Number(id))).unwrap();
      dispatch(setAlert({ type: "success", msg: t("pages.agents.agentDeleted") }));
      navigate("/agents");
    } catch (err) {
      console.error("Failed to delete agent:", err);
    }
  };

  if (loading) {
    return (
      <div className="h-full relative">
        <Spinner />
      </div>
    );
  }

  // Scrolling and centering must stay on two separate elements: a box that both centers and
  // scrolls pushes the top of an overflowing card above scrollTop 0, where it is unreachable
  // (the Name field vanished at >=125% zoom). min-h-full keeps the card centered when it fits.
  return (
    <>
      <div className="h-full overflow-y-auto">
        <div className="min-h-full flex items-center justify-center py-4">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 w-full max-w-2xl">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <AgentConfigFields register={register} errors={errors} control={control} />

              {/* Actions */}
              <div className="flex justify-end items-center gap-3 pt-4 border-t border-gray-200">
                {isEdit && (
                  <button
                    type="button"
                    onClick={() => dispatch(openConfirmationModal(DELETE_AGENT_MODAL_ID))}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors mr-auto"
                  >
                    {t("pages.agents.deleteAgent")}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="btn-alternative-outline text-sm"
                >
                  {t("general.cancel")}
                </button>
                <button type="submit" disabled={submitting} className="btn-primary-fill text-sm">
                  {submitting
                    ? "..."
                    : isEdit
                    ? t("pages.agents.editAgent")
                    : t("pages.agents.createAgent")}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {isEdit && (
        <ModalConfirmation
          id={DELETE_AGENT_MODAL_ID}
          title={t("pages.agents.deleteAgent")}
          text={t("pages.agents.deleteAgentConfirm")}
          onClickSubmit={handleDelete}
        />
      )}
    </>
  );
};

export default AgentForm;
